// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import React from 'react'
import styled, { useTheme } from 'styled-components'

import type {
  Language,
  PublicUnit
} from 'lib-common/generated/api-types/daycare'
import type { Theme } from 'lib-common/theme'
import { StaticChip } from 'lib-components/atoms/Chip'
import ExternalLink from 'lib-components/atoms/ExternalLink'
import { Button } from 'lib-components/atoms/buttons/Button'
import { IconOnlyButton } from 'lib-components/atoms/buttons/IconOnlyButton'
import { tabletMin } from 'lib-components/breakpoints'
import {
  FixedSpaceColumn,
  FixedSpaceFlexWrap
} from 'lib-components/layout/flex-helpers'
import { BigNumber, H4 } from 'lib-components/typography'
import { defaultMargins, Gap } from 'lib-components/white-space'
import { faArrowDown, faArrowUp, faTimes } from 'lib-icons'

import type { ApplicationEditorDeps } from '../types'

export type PreferredUnitBoxProps = {
  deps: ApplicationEditorDeps
  unit: PublicUnit
  n: number
  remove: () => void
  moveUp: (() => void) | null
  moveDown: (() => void) | null
}

export default React.memo(function PreferredUnitBox({
  deps,
  unit,
  n,
  remove,
  moveUp,
  moveDown
}: PreferredUnitBoxProps) {
  const { translations: t } = deps
  const { colors } = useTheme()

  const providerTypeText =
    unit.providerType === 'PRIVATE_SERVICE_VOUCHER'
      ? t.common.unit.providerTypes.PRIVATE
      : t.common.unit.providerTypes[unit.providerType]

  const getProviderTypeColors = (): { $color: string } => {
    switch (unit.providerType) {
      case 'MUNICIPAL':
      case 'MUNICIPAL_SCHOOL':
        return { $color: colors.accents.a6turquoise }
      case 'PRIVATE':
      case 'PRIVATE_SERVICE_VOUCHER':
        return { $color: colors.accents.a3emerald }
      case 'PURCHASED':
      case 'EXTERNAL_PURCHASED':
        return { $color: colors.main.m4 }
    }
  }

  return (
    <Wrapper data-qa={`preferred-unit-${unit.id}`}>
      <MainColLeft>
        <BigNumber>{n}</BigNumber>
      </MainColLeft>
      <MainColCenter>
        <FixedSpaceColumn>
          <FixedSpaceColumn $spacing="xxs">
            <H4
              aria-label={t.applications.editor.unitPreference.units.preferences.preferenceNameAndNumber(
                n,
                unit.name
              )}
              $noMargin
            >
              {unit.name}
            </H4>
            <span>{unit.streetAddress}</span>
            {unit.providerType === 'PRIVATE_SERVICE_VOUCHER' && (
              <ExternalLink
                href={
                  t.applications.editor.unitPreference.units.serviceVoucherLink
                }
                text={t.common.unit.providerTypes.PRIVATE_SERVICE_VOUCHER}
                newTab
              />
            )}
          </FixedSpaceColumn>
          <FixedSpaceFlexWrap $horizontalSpacing="xs" $verticalSpacing="xs">
            <StaticChip $color={unitLanguageColors(colors)[unit.language]}>
              {
                t.applications.editor.unitPreference.units.preferences[
                  unit.language
                ]
              }
            </StaticChip>
            <StaticChip {...getProviderTypeColors()}>
              {providerTypeText.toLowerCase()}
            </StaticChip>
          </FixedSpaceFlexWrap>
          <Gap $size="xs" />
          <FixedSpaceFlexWrap $horizontalSpacing="xs">
            <Button
              appearance="inline"
              text={
                t.applications.editor.unitPreference.units.preferences.moveUp
              }
              aria-label={`${unit.name} (${
                t.applications.editor.unitPreference.units.preferences[
                  unit.language
                ]
              }, ${providerTypeText}): ${
                t.applications.editor.unitPreference.units.preferences.moveUp
              }`}
              icon={faArrowUp}
              onClick={moveUp || noOp}
              disabled={!moveUp}
            />
            <Button
              appearance="inline"
              text={
                t.applications.editor.unitPreference.units.preferences.moveDown
              }
              aria-label={`${unit.name} (${
                t.applications.editor.unitPreference.units.preferences[
                  unit.language
                ]
              }, ${providerTypeText}): ${
                t.applications.editor.unitPreference.units.preferences.moveDown
              }`}
              icon={faArrowDown}
              onClick={moveDown || noOp}
              disabled={!moveDown}
            />
          </FixedSpaceFlexWrap>
        </FixedSpaceColumn>
      </MainColCenter>
      <MainColRight>
        <IconOnlyButton
          icon={faTimes}
          color="gray"
          onClick={remove}
          aria-label={`${unit.name}: ${t.applications.editor.unitPreference.units.preferences.remove}`}
        />
      </MainColRight>
    </Wrapper>
  )
})

const unitLanguageColors = (
  colors: Theme['colors']
): Record<Language, string> => ({
  fi: colors.main.m3,
  sv: colors.accents.a5orangeLight,
  en: colors.accents.a10powder
})

const noOp = () => undefined

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${(p) => p.theme.colors.main.m2};
  box-sizing: border-box;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  border-radius: 2px;
`

const MainColLeft = styled.div`
  flex: 0 0 52px;
  text-align: center;
  padding: ${defaultMargins.xs};

  @media screen and (min-width: ${tabletMin}) {
    flex-basis: 64px;
    padding: ${defaultMargins.s};
  }
`

const MainColCenter = styled.div`
  flex-grow: 1;
  padding: ${defaultMargins.s} 0;
`

const MainColRight = styled.div`
  flex-grow: 0;
  padding: ${defaultMargins.s};
`
